import {
  CreateSchedulePlanRequest,
  SchedulePlanRecurrenceFrequency,
} from "apis/SchedulePlanAPI";
import {
  CreateScheduleRequest,
  MultiScheduleCreate,
} from "apis/ScheduleAPI";
import { AutocompleteOption } from "components/AutocompleteSelect";
import * as Yup from "yup";
import { Availability } from "./MultiScheduleSelect";

export interface ScheduleModalFormValues {
  selectedContact: AutocompleteOption | null;
  sportCourtId: string;
  date: Date | undefined;
  availability: Availability[];
  observation: string;
  status: string;
  isRecurring: boolean;
  startDate: Date | undefined;
  endDate: Date | undefined;
  recurrenceFrequency: SchedulePlanRecurrenceFrequency;
  recurrenceDaysOfWeek: string[];
}

export const RECURRENCE_FREQUENCY_OPTIONS: Array<{
  value: SchedulePlanRecurrenceFrequency;
  label: string;
}> = [
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensal" },
];

export const DAY_OF_WEEK_OPTIONS = [
  { value: "SUNDAY", label: "Dom" },
  { value: "MONDAY", label: "Seg" },
  { value: "TUESDAY", label: "Ter" },
  { value: "WEDNESDAY", label: "Qua" },
  { value: "THURSDAY", label: "Qui" },
  { value: "FRIDAY", label: "Sex" },
  { value: "SATURDAY", label: "Sab" },
] as const;

const weekDayByIndex = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;

const formatDateOnly = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

export const toApiDateTime = (date: Date): string =>
  `${formatDateOnly(date)}T00:00:00Z`;

export const getDayOfWeekFromDate = (date?: Date): string[] => {
  if (!date || Number.isNaN(date.getTime())) {
    return ["MONDAY"];
  }

  return [weekDayByIndex[date.getDay()]];
};

export const shouldRequireDaysOfWeek = (
  frequency: SchedulePlanRecurrenceFrequency
): boolean => frequency === "WEEKLY" || frequency === "FORTNIGHTLY";

export const normalizeAvailabilitySelection = (
  nextAvailability: Availability[],
  isRecurring: boolean
): Availability[] => {
  if (!isRecurring) {
    return nextAvailability;
  }

  if (nextAvailability.length <= 1) {
    return nextAvailability;
  }

  return [nextAvailability[nextAvailability.length - 1]];
};

export const buildScheduleCreatePayload = ({
  formValues,
  selectedContact,
  selectedAvailability,
  responsibleId,
}: {
  formValues: ScheduleModalFormValues;
  selectedContact: AutocompleteOption;
  selectedAvailability: Availability[];
  responsibleId: string;
}): MultiScheduleCreate => {
  const schedules: CreateScheduleRequest[] = selectedAvailability.map(
    (availabilityItem) => {
      const [startTime] = availabilityItem.timeFromTo.split(" - ");
      const dateTimeString = `${formatDateOnly(
        formValues.date || new Date()
      )}T${startTime}:00-03:00`;

      return {
        contacts: [
          {
            id: selectedContact.id,
            name: selectedContact.label,
          },
        ],
        responsibleId,
        observation: formValues.observation || "",
        rentalData: {
          sportCourtId: formValues.sportCourtId,
          sportCourtValue: availabilityItem.value,
          sportCourtRentalTime: availabilityItem.rentalTime,
          rentalQuantity: 1,
        },
        status: formValues.status,
        type: "RENT",
        timeFromTo: availabilityItem.timeFromTo,
        whenDate: new Date(dateTimeString),
      };
    }
  );

  return {
    schedules,
    notify: true,
  };
};

export const buildSchedulePlanPayload = ({
  formValues,
  selectedContact,
  selectedAvailability,
  responsibleId,
}: {
  formValues: ScheduleModalFormValues;
  selectedContact: AutocompleteOption;
  selectedAvailability: Availability[];
  responsibleId: string;
}): CreateSchedulePlanRequest => {
  const normalizedAvailability = normalizeAvailabilitySelection(
    selectedAvailability,
    true
  );

  if (!normalizedAvailability.length) {
    throw new Error("Selecione um horario para o plano recorrente.");
  }

  const [availabilityItem] = normalizedAvailability;

  if (!formValues.startDate || !formValues.endDate) {
    throw new Error("Informe as datas inicial e final da recorrencia.");
  }

  const normalizedFrequency = formValues.recurrenceFrequency;
  const normalizedDaysOfWeek = shouldRequireDaysOfWeek(normalizedFrequency)
    ? formValues.recurrenceDaysOfWeek.length > 0
      ? formValues.recurrenceDaysOfWeek
      : getDayOfWeekFromDate(formValues.startDate)
    : [];

  return {
    type: "INDIVIDUAL",
    contacts: [
      {
        id: selectedContact.id,
        name: selectedContact.label,
      },
    ],
    responsibleId,
    sportCourtId: formValues.sportCourtId,
    timeFromTo: availabilityItem.timeFromTo,
    startDate: toApiDateTime(formValues.startDate),
    endDate: toApiDateTime(formValues.endDate),
    recurrence: {
      frequency: normalizedFrequency,
      interval: 1,
      daysOfWeek: normalizedDaysOfWeek,
    },
  };
};

export const scheduleModalValidationSchema =
  Yup.object().shape({
    selectedContact: Yup.object().nullable().required("Cliente e obrigatorio!"),
    sportCourtId: Yup.string().required("Quadra e obrigatoria!"),
    date: Yup.date().required("A data e obrigatoria"),
    availability: Yup.array()
      .min(1, "Selecione pelo menos uma opcao")
      .test(
        "single-availability-when-recurring",
        "Selecione apenas um horario para o plano recorrente.",
        function validateAvailability(value) {
          const { isRecurring } = this.parent as ScheduleModalFormValues;

          if (!isRecurring) {
            return Array.isArray(value) && value.length > 0;
          }

          return Array.isArray(value) && value.length === 1;
        }
      ),
    isRecurring: Yup.boolean().required(),
    startDate: Yup.date().when("isRecurring", {
      is: true,
      then: (schema) => schema.required("A data inicial e obrigatoria."),
      otherwise: (schema) => schema.notRequired(),
    }),
    endDate: Yup.date().when("isRecurring", {
      is: true,
      then: (schema) =>
        schema
          .required("A data final e obrigatoria.")
          .test(
            "end-date-after-start-date",
            "A data final deve ser igual ou posterior a data inicial.",
            function validateEndDate(value) {
              const { startDate } = this.parent as ScheduleModalFormValues;

              if (!value || !startDate) {
                return false;
              }

              return value.getTime() >= startDate.getTime();
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    recurrenceFrequency: Yup.string().when("isRecurring", {
      is: true,
      then: (schema) => schema.required("A frequencia e obrigatoria."),
      otherwise: (schema) => schema.notRequired(),
    }),
    recurrenceDaysOfWeek: Yup.array().when(
      ["isRecurring", "recurrenceFrequency"],
      {
        is: (isRecurring: boolean, recurrenceFrequency: SchedulePlanRecurrenceFrequency) =>
          Boolean(isRecurring) && shouldRequireDaysOfWeek(recurrenceFrequency),
        then: (schema) =>
          schema.min(
            1,
            "Selecione ao menos um dia da semana para a recorrencia."
          ),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
  });
